import { toast, type Id, type ToastOptions } from "react-toastify";

/**
 * Single integration point for in-app non-blocking feedback. Every feature
 * imports `notify.*` rather than `react-toastify` directly so the underlying
 * library can be swapped without touching callsites.
 *
 * Modal confirms (timer toggle, turn alert) stay in `Modal` — toasts are
 * strictly for non-blocking notices.
 */

const SETTINGS_KEY = "notifications.settings";

type NotificationSettings = {
  muted: boolean;
};

function readSettings(): NotificationSettings {
  if (typeof window === "undefined") return { muted: false };
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { muted: false };
    const parsed = JSON.parse(raw) as Partial<NotificationSettings>;
    return { muted: !!parsed.muted };
  } catch {
    return { muted: false };
  }
}

export function getNotificationSettings(): NotificationSettings {
  return readSettings();
}

export function setNotificationSettings(next: NotificationSettings) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
}

function call(kind: "success" | "error" | "info" | "warning", message: string, options?: ToastOptions): Id | null {
  if (readSettings().muted) return null;
  return toast[kind](message, options);
}

export const notify = {
  success: (message: string, options?: ToastOptions) => call("success", message, options),
  error: (message: string, options?: ToastOptions) => call("error", message, options),
  info: (message: string, options?: ToastOptions) => call("info", message, options),
  warning: (message: string, options?: ToastOptions) => call("warning", message, options),
  dismissAll: () => toast.dismiss(),
};
